import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ServiceScreenshot } from '../../types';
import styles from './AppScreenshots.module.css';

interface AppScreenshotsProps {
  screenshots: ServiceScreenshot[];
  accentColor?: string;
}

/**
 * Scroll-driven showcase: on desktop a phone stays pinned mid-screen and
 * crossfades to the matching screenshot as each feature's text scrolls past.
 * On mobile it collapses to phone-above-text cards.
 */
const AppScreenshots: React.FC<AppScreenshotsProps> = ({ screenshots, accentColor }) => {
  const { t, i18n } = useTranslation();
  const [active, setActive] = useState(0);
  const blockRefs = useRef<Array<HTMLDivElement | null>>([]);

  const language = i18n.language.split('-')[0];
  const srcFor = (shot: ServiceScreenshot) => shot.src[language] ?? shot.src.hu;

  useEffect(() => {
    const blocks = blockRefs.current.filter(Boolean) as HTMLDivElement[];
    if (blocks.length === 0) return;

    // A block becomes active while it crosses the vertical middle of the viewport.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(Number((entry.target as HTMLElement).dataset.index));
          }
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );

    blocks.forEach((block) => observer.observe(block));
    return () => observer.disconnect();
  }, [screenshots.length]);

  if (screenshots.length === 0) return null;

  return (
    <section className={styles.section}>
      <div className="container">
        <h2 className={styles.title}>{t('services.screenshotsTitle')}</h2>
        <div className={styles.showcase}>
          <div className={styles.phoneColumn}>
            <div className={styles.stickyWrap}>
              <div className={styles.phoneStack}>
                <div
                  className={styles.phoneGlow}
                  style={
                    accentColor
                      ? {
                          background: `radial-gradient(circle at 50% 35%, ${accentColor} 0%, transparent 65%)`,
                        }
                      : undefined
                  }
                />
                {screenshots.map((shot, index) => (
                  <img
                    key={shot.id}
                    src={srcFor(shot)}
                    alt={t(shot.titleKey)}
                    width={620}
                    height={1293}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    className={`${styles.phoneImage} ${index === active ? styles.phoneImageActive : ''}`}
                  />
                ))}
              </div>
              <div className={styles.dots} role="tablist">
                {screenshots.map((shot, index) => (
                  <button
                    key={shot.id}
                    type="button"
                    role="tab"
                    aria-selected={index === active}
                    aria-label={t(shot.titleKey)}
                    className={`${styles.dot} ${index === active ? styles.dotActive : ''}`}
                    style={index === active && accentColor ? { backgroundColor: accentColor } : undefined}
                    onClick={() =>
                      blockRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                    }
                  />
                ))}
              </div>
            </div>
          </div>

          <div className={styles.textColumn}>
            {screenshots.map((shot, index) => (
              <div
                key={shot.id}
                ref={(el) => {
                  blockRefs.current[index] = el;
                }}
                data-index={index}
                className={`${styles.block} ${index === active ? styles.blockActive : ''}`}
              >
                <img
                  src={srcFor(shot)}
                  alt={t(shot.titleKey)}
                  width={620}
                  height={1293}
                  loading="lazy"
                  className={styles.blockPhone}
                />
                <span
                  className={styles.blockIndex}
                  style={accentColor ? { color: accentColor } : undefined}
                >
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h3 className={styles.blockTitle}>{t(shot.titleKey)}</h3>
                <p className={styles.blockDescription}>{t(shot.descriptionKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppScreenshots;
